import ExcelJS from "exceljs";

const isBlocked = () => /FBAN|FBAV|Zalo|Messenger|Instagram/i.test(navigator.userAgent || "");

// Xuất 1 bảng đơn giản ra Excel có định dạng: dòng tiêu đề, header đậm, kẻ ô, số có phân cách hàng nghìn,
// dòng Tổng cộng đậm. header: ["Cột A", ...]; rows: [[...]] (số để nguyên dạng số); moneyCols: chỉ số cột (0-based)
// định dạng tiền #,##0; totalRow: mảng cùng độ dài header (tuỳ chọn).
// CÔNG THỨC (để kế toán sửa số trên Excel là tự tính lại):
//  - totalRow: ô số nào bằng đúng tổng cột -> ghi công thức =SUM(cột) (khác thì giữ nguyên số).
//  - rowFormulas: [{ col, refs: [chỉ số cột...], f: (refs: ["D3", ...]) => "SUM(D3,E3)-F3", calc: (nums) => số }]
//    -> mỗi dòng, nếu calc(nums) khớp số đang có (lệch < 1đ) thì ghi công thức f(...), không khớp giữ nguyên số.
export const colLetter = (i) => { let n = i + 1, s = ""; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };
const numOf = (v) => (typeof v === "number" ? v : (v && typeof v === "object" && "result" in v ? Number(v.result) || 0 : 0));
function applyFormulas(rows, totalRow, rowFormulas) {
  const outRows = rows.map((vals, ri) => {
    const rowNo = 3 + ri;
    const out = [...vals];
    (rowFormulas || []).forEach((rf) => {
      const cur = vals[rf.col];
      if (typeof cur !== "number") return;
      const nums = rf.refs.map((c) => numOf(vals[c]));
      const calc = rf.calc(nums);
      if (!Number.isFinite(calc) || Math.abs(calc - cur) >= 1) return;
      out[rf.col] = { formula: rf.f(rf.refs.map((c) => `${colLetter(c)}${rowNo}`)), result: cur };
    });
    return out;
  });
  let outTotal = totalRow;
  if (totalRow && rows.length) {
    const last = 2 + rows.length;
    outTotal = totalRow.map((v, i) => {
      if (typeof v !== "number") return v;
      const sum = rows.reduce((a, r) => a + numOf(r[i]), 0);
      return Math.abs(sum - v) < 1 ? { formula: `SUM(${colLetter(i)}3:${colLetter(i)}${last})`, result: v } : v;
    });
  }
  return { outRows, outTotal };
}
export async function exportTableExcel(opts) {
  if (isBlocked()) {
    window.alert("Trình duyệt trong ứng dụng (Zalo/Messenger/...) không cho phép tải file.\n\nHãy mở bằng Safari/Chrome rồi bấm Export Excel lại.");
    return;
  }
  // Nhiều sheet: { fileName, sheets: [{ title, sheetName, header, rows, moneyCols, totalRow }] }; 1 sheet: truyền thẳng các trường.
  const sheets = opts.sheets || [opts];
  const wb = new ExcelJS.Workbook();
  const font = { name: "Times New Roman", size: 12 };
  const thin = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
  sheets.forEach((sh) => {
    const { title, sheetName, header, moneyCols = [], rowFormulas = null } = sh;
    const { outRows: rows, outTotal: totalRow } = applyFormulas(sh.rows, sh.totalRow || null, rowFormulas);
    const ws = wb.addWorksheet(String(sheetName || "Sheet1").slice(0, 31));
    const ncol = header.length;
    ws.mergeCells(1, 1, 1, ncol);
    const t = ws.getCell(1, 1);
    t.value = title || "";
    t.font = { ...font, size: 14, bold: true };
    t.alignment = { horizontal: "center", vertical: "middle" };
    ws.getRow(1).height = 26;
    const hr = ws.getRow(2);
    header.forEach((h, i) => {
      const c = hr.getCell(i + 1);
      c.value = h; c.font = { ...font, bold: true }; c.border = thin;
      c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    });
    hr.height = 32;
    const money = new Set(moneyCols);
    const put = (rowNo, vals, bold) => {
      const r = ws.getRow(rowNo);
      vals.forEach((v, i) => {
        const c = r.getCell(i + 1);
        c.value = v === undefined || v === null ? "" : v;
        c.font = bold ? { ...font, bold: true } : font; c.border = thin;
        const isNum = typeof v === "number" || (v && typeof v === "object" && "formula" in v); // ô công thức {formula, result}
        c.alignment = { vertical: "middle", wrapText: true, horizontal: isNum ? "right" : (i === 0 ? "left" : "center") };
        if (isNum) c.numFmt = money.has(i) ? "#,##0" : "0.##";
        if (bold) c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFECFDF5" } };
      });
    };
    rows.forEach((vals, i) => put(3 + i, vals, false));
    if (totalRow) put(3 + rows.length, totalRow, true);
    header.forEach((h, i) => {
      const maxLen = Math.max(String(h).length, ...rows.map((r) => { const v = r[i] && typeof r[i] === "object" && "result" in r[i] ? r[i].result : r[i]; return String(v === undefined || v === null ? "" : v).length; }), 6);
      ws.getColumn(i + 1).width = Math.min(46, Math.max(10, Math.round(maxLen * 1.15) + 2));
    });
    ws.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
  });
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = opts.fileName;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
